import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, Lock, RefreshCw, Database, FileText, 
  Check, X, Play, LayoutGrid, Users, Image, 
  ChevronDown, ChevronUp, ExternalLink, Zap, 
  Clock, CheckCircle, AlertCircle, SkipForward, Pencil,
  Settings, Key, CircleCheck, CircleX
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pipeline status badge colors
const STATUS_COLORS = {
  SCRAPED: 'bg-gray-600 text-gray-100',
  CONTENT_GENERATED: 'bg-blue-600 text-blue-100',
  CURATING: 'bg-yellow-600 text-yellow-100',
  READY: 'bg-green-600 text-green-100',
  PUBLISHED: 'bg-[#84cc16] text-black',
  SKIPPED: 'bg-red-900 text-red-200',
  LEGACY: 'bg-purple-600 text-purple-100'
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [pipelineData, setPipelineData] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  
  // Action states
  const [scraping, setScraping] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generatingSlug, setGeneratingSlug] = useState(null);
  const [publishingSlug, setPublishingSlug] = useState(null);
  const [skippingSlug, setSkippingSlug] = useState(null);
  
  // UI states
  const [showPublished, setShowPublished] = useState(false);
  
  // LLM Settings states
  const [llmSettings, setLlmSettings] = useState(null);
  const [llmProvider, setLlmProvider] = useState('emergent');
  const [llmModel, setLlmModel] = useState('claude-haiku-4-5-20251001');
  const [llmApiKey, setLlmApiKey] = useState('');
  const [llmTesting, setLlmTesting] = useState(false);
  const [llmSaving, setLlmSaving] = useState(false);
  const [llmTestResult, setLlmTestResult] = useState(null);
  const [showLlmSettings, setShowLlmSettings] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_password', password);
      toast.success('Admin access granted');
    } catch (error) {
      toast.error('Invalid admin password');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!password) return;
    try {
      const [pipelineRes, statsRes, logsRes, llmRes] = await Promise.all([
        axios.get(`${API}/admin/pipeline?password=${password}`),
        axios.get(`${API}/admin/stats?password=${password}`),
        axios.get(`${API}/admin/logs?password=${password}&limit=20`),
        axios.get(`${API}/admin/llm-settings?password=${password}`)
      ]);
      setPipelineData(pipelineRes.data);
      setStats(statsRes.data);
      setLogs(logsRes.data);
      setLlmSettings(llmRes.data);
      setLlmProvider(llmRes.data.provider);
      setLlmModel(llmRes.data.model);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  }, [password]);

  useEffect(() => {
    const savedPassword = sessionStorage.getItem('admin_password');
    if (savedPassword) {
      setPassword(savedPassword);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && password) {
      fetchData();
    }
  }, [isAuthenticated, password, fetchData]);

  // Scrape Imgflip
  const handleScrape = async () => {
    setScraping(true);
    try {
      const response = await axios.post(`${API}/admin/scrape-imgflip?password=${password}&pages=2`);
      toast.success(`Found ${response.data.new_templates} new templates (${response.data.already_existed} already existed)`);
      fetchData();
    } catch (error) {
      toast.error('Failed to scrape Imgflip: ' + (error.response?.data?.detail || error.message));
    } finally {
      setScraping(false);
    }
  };

  // Generate all content
  const handleGenerateAll = async () => {
    setGeneratingAll(true);
    try {
      const response = await axios.post(`${API}/admin/generate-all-content?password=${password}`);
      const { success, failed } = response.data;
      toast.success(`Generated: ${success.length}, Failed: ${failed.length}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate all content');
    } finally {
      setGeneratingAll(false);
    }
  };

  // Generate content for single template
  const handleGenerateContent = async (slug) => {
    setGeneratingSlug(slug);
    try {
      await axios.post(`${API}/admin/generate-content/${slug}?password=${password}`);
      toast.success(`Content generated for ${slug}`);
      fetchData();
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGeneratingSlug(null);
    }
  };

  // Publish template
  const handlePublish = async (slug) => {
    setPublishingSlug(slug);
    try {
      const response = await axios.post(`${API}/admin/publish/${slug}?password=${password}`);
      toast.success(`Published! URL: ${response.data.url}`);
      fetchData();
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setPublishingSlug(null);
    }
  };

  // Skip template
  const handleSkip = async (slug) => {
    setSkippingSlug(slug);
    try {
      await axios.post(`${API}/admin/pipeline/${slug}/status?password=${password}`, {
        new_status: 'SKIPPED'
      });
      toast.success('Template skipped');
      fetchData();
    } catch (error) {
      toast.error('Failed to skip template');
    } finally {
      setSkippingSlug(null);
    }
  };

  // LLM Settings handlers
  const handleLlmTest = async () => {
    setLlmTesting(true);
    setLlmTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/llm-test?password=${password}`, {
        provider: llmProvider,
        model: llmModel,
        api_key: llmProvider !== 'emergent' ? llmApiKey : null
      });
      setLlmTestResult(res.data);
    } catch (error) {
      setLlmTestResult({ success: false, error: error.response?.data?.detail || error.message });
    } finally {
      setLlmTesting(false);
    }
  };

  const handleLlmSave = async () => {
    setLlmSaving(true);
    try {
      await axios.put(`${API}/admin/llm-settings?password=${password}`, {
        provider: llmProvider,
        model: llmModel,
        api_key: llmProvider !== 'emergent' ? llmApiKey : null
      });
      toast.success(`LLM provider set to ${llmProvider} / ${llmModel}`);
      setLlmApiKey('');
      setLlmTestResult(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLlmSaving(false);
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-full max-w-md p-8 bg-[#18181b] border border-[#27272a] rounded-xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#bef264]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#bef264]" />
            </div>
            <h1 className="text-2xl font-bold text-white font-['Space_Grotesk']">Admin Access</h1>
            <p className="text-[#a1a1aa] mt-2">Enter admin password to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="bg-[#09090b] border-[#27272a] text-white"
              data-testid="admin-password-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#bef264] text-black hover:bg-[#a3e635]"
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Access Admin'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const queue = pipelineData?.queue || [];
  const counts = pipelineData?.counts || {};
  const publishedTemplates = queue.filter(t => t.pipeline_status === 'PUBLISHED');
  const activeQueue = queue.filter(t => !['PUBLISHED', 'SKIPPED'].includes(t.pipeline_status));

  return (
    <div className="min-h-screen bg-[#09090b] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Space_Grotesk']">Content Pipeline</h1>
            <p className="text-[#a1a1aa] mt-1">Manage template scraping, content generation, and publishing</p>
          </div>
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-[#27272a] text-white hover:bg-white/5"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* ====== SECTION 1: COCKPIT HEADER ====== */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Due Work - Highlighted */}
          <div className="bg-[#18181b] border-2 border-[#bef264] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Zap className="w-5 h-5 text-[#bef264]" />
              <span className="text-[#bef264] text-sm font-medium">Due Work</span>
            </div>
            <p className="text-4xl font-bold text-[#bef264]">{counts.due_work || 0}</p>
            <p className="text-xs text-[#a1a1aa] mt-1">Templates to process</p>
          </div>
          
          {/* Published */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-[#a1a1aa] text-sm">Published</span>
            </div>
            <p className="text-4xl font-bold text-white">{counts.PUBLISHED || 0}</p>
          </div>
          
          {/* Content Generated */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-blue-500" />
              <span className="text-[#a1a1aa] text-sm">Content Ready</span>
            </div>
            <p className="text-4xl font-bold text-white">{counts.CONTENT_GENERATED || 0}</p>
          </div>
          
          {/* Scraped */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-gray-500" />
              <span className="text-[#a1a1aa] text-sm">Scraped (Pending)</span>
            </div>
            <p className="text-4xl font-bold text-white">{counts.SCRAPED || 0}</p>
          </div>
        </div>

        {/* ====== SECTION 2: ACTION BAR ====== */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleScrape}
              disabled={scraping}
              className="bg-[#bef264] text-black hover:bg-[#a3e635]"
              data-testid="scrape-imgflip-btn"
            >
              {scraping ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              Scrape Imgflip (Pages 1-2)
            </Button>
            
            <Button
              onClick={handleGenerateAll}
              disabled={generatingAll}
              variant="outline"
              className="border-[#27272a] text-white hover:bg-white/5"
              data-testid="generate-all-btn"
            >
              {generatingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Generate All AI Content
            </Button>
          </div>
        </div>

        {/* ====== LLM SETTINGS (Collapsible) ====== */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl overflow-hidden mb-8">
          <button
            onClick={() => setShowLlmSettings(!showLlmSettings)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-[#1c1c1f] transition-colors"
            data-testid="llm-settings-toggle"
          >
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-[#bef264]" />
              <h2 className="text-lg font-semibold text-white">
                LLM Settings
              </h2>
              {llmSettings && (
                <Badge className="bg-[#27272a] text-[#a1a1aa] ml-2">
                  {llmSettings.provider} / {llmSettings.model.split('-').slice(0, 2).join('-')}
                </Badge>
              )}
            </div>
            {showLlmSettings ? <ChevronUp className="w-5 h-5 text-[#a1a1aa]" /> : <ChevronDown className="w-5 h-5 text-[#a1a1aa]" />}
          </button>

          {showLlmSettings && llmSettings && (
            <div className="border-t border-[#27272a] p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Provider */}
                <div>
                  <label className="text-xs text-[#a1a1aa] block mb-1.5">Provider</label>
                  <select
                    value={llmProvider}
                    onChange={(e) => {
                      const p = e.target.value;
                      setLlmProvider(p);
                      const models = llmSettings.available_models[p];
                      if (models && models.length > 0) setLlmModel(models[0].id);
                      setLlmTestResult(null);
                      setLlmApiKey('');
                    }}
                    className="w-full bg-[#09090b] border border-[#27272a] text-white rounded-md px-3 py-2 text-sm"
                    data-testid="llm-provider-select"
                  >
                    <option value="emergent">Emergent (Built-in Key)</option>
                    <option value="openai">OpenAI (Your API Key)</option>
                    <option value="anthropic">Anthropic (Your API Key)</option>
                  </select>
                </div>

                {/* Model */}
                <div>
                  <label className="text-xs text-[#a1a1aa] block mb-1.5">Model</label>
                  <select
                    value={llmModel}
                    onChange={(e) => { setLlmModel(e.target.value); setLlmTestResult(null); }}
                    className="w-full bg-[#09090b] border border-[#27272a] text-white rounded-md px-3 py-2 text-sm"
                    data-testid="llm-model-select"
                  >
                    {(llmSettings.available_models[llmProvider] || []).map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>

                {/* API Key (only for OpenAI/Anthropic) */}
                <div>
                  <label className="text-xs text-[#a1a1aa] block mb-1.5">
                    {llmProvider === 'emergent' ? 'API Key (auto from env)' : 'API Key'}
                  </label>
                  {llmProvider === 'emergent' ? (
                    <div className="flex items-center gap-2 bg-[#09090b] border border-[#27272a] rounded-md px-3 py-2">
                      <Key className="w-4 h-4 text-[#bef264]" />
                      <span className="text-sm text-[#a1a1aa]">
                        {llmSettings.has_key ? 'Configured' : 'Not set'}
                      </span>
                    </div>
                  ) : (
                    <Input
                      type="password"
                      value={llmApiKey}
                      onChange={(e) => { setLlmApiKey(e.target.value); setLlmTestResult(null); }}
                      placeholder={llmSettings.has_key && llmProvider === llmSettings.provider ? '••••••• (saved)' : `Paste your ${llmProvider === 'openai' ? 'OpenAI' : 'Anthropic'} key`}
                      className="bg-[#09090b] border-[#27272a] text-white"
                      data-testid="llm-api-key-input"
                    />
                  )}
                </div>
              </div>

              {/* Test result */}
              {llmTestResult && (
                <div className={`flex items-center gap-2 mb-4 p-3 rounded-lg text-sm ${
                  llmTestResult.success ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                }`}>
                  {llmTestResult.success ? <CircleCheck className="w-4 h-4" /> : <CircleX className="w-4 h-4" />}
                  {llmTestResult.success ? 'Connection successful!' : `Error: ${llmTestResult.error}`}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={handleLlmTest}
                  disabled={llmTesting || (llmProvider !== 'emergent' && !llmApiKey && !llmSettings.has_key)}
                  variant="outline"
                  className="border-[#27272a] text-white hover:bg-white/5"
                  data-testid="llm-test-btn"
                >
                  {llmTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  Test Connection
                </Button>
                <Button
                  onClick={handleLlmSave}
                  disabled={llmSaving || (llmProvider !== 'emergent' && !llmApiKey && !(llmSettings.has_key && llmProvider === llmSettings.provider))}
                  className="bg-[#bef264] text-black hover:bg-[#a3e635]"
                  data-testid="llm-save-btn"
                >
                  {llmSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Settings
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ====== SECTION 3: PIPELINE QUEUE TABLE ====== */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl overflow-hidden mb-8">
          <div className="p-4 border-b border-[#27272a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">
              Pipeline Queue ({activeQueue.length})
            </h2>
            <div className="flex gap-2">
              {Object.entries(STATUS_COLORS).slice(0, 5).map(([status, color]) => (
                <div key={status} className="flex items-center gap-1 text-xs">
                  <div className={`w-3 h-3 rounded ${color.split(' ')[0]}`}></div>
                  <span className="text-[#a1a1aa]">{status.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-[#09090b] sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-[#a1a1aa]">Thumbnail</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-[#a1a1aa]">Name</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-[#a1a1aa]">Rank</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-[#a1a1aa]">Status</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-[#a1a1aa]">Content</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-[#a1a1aa]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272a]">
                {activeQueue.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-[#a1a1aa]">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No templates in queue. Click "Scrape Imgflip" to add templates.
                    </td>
                  </tr>
                ) : (
                  activeQueue.map((template) => (
                    <tr key={template.slug} className="hover:bg-[#1c1c1f]">
                      <td className="px-4 py-3">
                        <img 
                          src={template.thumbnail_url} 
                          alt={template.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-white font-medium">{template.name}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-[#a1a1aa]">#{template.imgflip_rank || '-'}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`${STATUS_COLORS[template.pipeline_status] || STATUS_COLORS.SCRAPED}`}>
                          {template.pipeline_status || 'SCRAPED'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {template.content_exists ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          {/* Generate Content Button */}
                          {template.pipeline_status === 'SCRAPED' && !template.content_exists && (
                            <Button
                              onClick={() => handleGenerateContent(template.slug)}
                              disabled={generatingSlug === template.slug}
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              {generatingSlug === template.slug ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Generate'
                              )}
                            </Button>
                          )}
                          
                          {/* Edit Button — visible for CONTENT_GENERATED, CURATING, READY */}
                          {['CONTENT_GENERATED', 'CURATING', 'READY'].includes(template.pipeline_status) && (
                            <Button
                              onClick={() => navigate(`/admin/edit/${template.slug}`)}
                              size="sm"
                              variant="outline"
                              className="border-[#27272a] text-white hover:bg-white/5"
                              data-testid={`edit-btn-${template.slug_lower}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Publish Button */}
                          {['CONTENT_GENERATED', 'CURATING', 'READY'].includes(template.pipeline_status) && (
                            <Button
                              onClick={() => handlePublish(template.slug)}
                              disabled={publishingSlug === template.slug}
                              size="sm"
                              className="bg-[#bef264] text-black hover:bg-[#a3e635]"
                            >
                              {publishingSlug === template.slug ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Publish'
                              )}
                            </Button>
                          )}
                          
                          {/* Skip Button */}
                          <Button
                            onClick={() => handleSkip(template.slug)}
                            disabled={skippingSlug === template.slug}
                            size="sm"
                            variant="outline"
                            className="border-red-900 text-red-400 hover:bg-red-900/20"
                          >
                            {skippingSlug === template.slug ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <SkipForward className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ====== SECTION 4: PUBLISHED TABLE (Collapsible) ====== */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl overflow-hidden mb-8">
          <button
            onClick={() => setShowPublished(!showPublished)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-[#1c1c1f] transition-colors"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-[#bef264]" />
              <h2 className="text-lg font-semibold text-white">
                Published Templates ({counts.PUBLISHED || 0})
              </h2>
            </div>
            {showPublished ? (
              <ChevronUp className="w-5 h-5 text-[#a1a1aa]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#a1a1aa]" />
            )}
          </button>
          
          {showPublished && (
            <div className="border-t border-[#27272a] max-h-[300px] overflow-y-auto">
              {publishedTemplates.length === 0 ? (
                <div className="p-8 text-center text-[#a1a1aa]">
                  No templates published yet
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4">
                  {publishedTemplates.map((template) => (
                    <div
                      key={template.slug}
                      className="group bg-[#09090b] border border-[#27272a] rounded-lg overflow-hidden hover:border-[#bef264] transition-colors"
                    >
                      <a
                        href={`/api/seo/meme/${template.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img 
                          src={template.thumbnail_url}
                          alt={template.name}
                          className="w-full aspect-square object-cover"
                        />
                      </a>
                      <div className="p-2">
                        <span className="text-xs text-white truncate block mb-1.5">{template.name}</span>
                        <button
                          onClick={() => navigate(`/admin/edit/${template.slug}`)}
                          className="flex items-center gap-1 text-[10px] text-[#bef264] hover:underline"
                          data-testid={`edit-published-${template.slug_lower}`}
                        >
                          <Pencil className="w-2.5 h-2.5" /> Edit Content
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ====== SECTION 5: STATS & LOGS ====== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Stats */}
          {stats && (
            <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Platform Stats</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#09090b] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <LayoutGrid className="w-4 h-4 text-[#bef264]" />
                    <span className="text-xs text-[#a1a1aa]">Total Templates</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{stats.total_templates}</p>
                </div>
                <div className="bg-[#09090b] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-blue-500" />
                    <span className="text-xs text-[#a1a1aa]">Content Generated</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{stats.content_generated}</p>
                </div>
                <div className="bg-[#09090b] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Image className="w-4 h-4 text-purple-500" />
                    <span className="text-xs text-[#a1a1aa]">Memes Created</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{stats.total_memes_created}</p>
                </div>
                <div className="bg-[#09090b] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-orange-500" />
                    <span className="text-xs text-[#a1a1aa]">Users</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                </div>
              </div>
            </div>
          )}

          {/* Logs */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#27272a]">
              <h2 className="text-lg font-semibold text-white">Monitor Logs</h2>
            </div>
            <div className="max-h-[250px] overflow-y-auto p-4">
              {logs.length === 0 ? (
                <p className="text-[#a1a1aa] text-center py-8">No logs yet</p>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <div 
                      key={`${log.action}-${log.checked_at}`}
                      className="flex items-center justify-between p-3 bg-[#09090b] rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          log.status === 'success' ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'
                        }`}>
                          {log.status}
                        </span>
                        <span className="text-white text-sm">{log.action || 'monitor'}</span>
                        {log.new_found_count > 0 && (
                          <span className="text-[#bef264] text-sm">+{log.new_found_count}</span>
                        )}
                      </div>
                      <span className="text-[#a1a1aa] text-xs">
                        {new Date(log.checked_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminPage;
